import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import {
  type Action_Bool_Exp,
  type Assessment_Bool_Exp,
  type Control_Bool_Exp,
  type Document_Bool_Exp,
  type Indicator_Bool_Exp,
  type Internal_Audit_Entity_Bool_Exp,
  type Issue_Bool_Exp,
  type Obligation_Bool_Exp,
  Parent_Type_Enum,
  type Risk_Bool_Exp,
  type Third_Party_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { getQueryVariables } from '@/utils/myItemsFilterConditions';

import { useDashboardStore } from '../../useDashboardStore';

/**
 * Builds up the gql filtering conditions based on the current filters set on the dashboard,
 * as well as any global entity filters that are set
 * @returns getQueryVariables function
 */
export const useGetQueryVariables = () => {
  const { myItemsFilters } = useDashboardStore();
  const { user } = useRisksmartUser();

  const vars = useMemo(
    () => getQueryVariables(myItemsFilters, user!.userId),
    [myItemsFilters, user]
  );

  return {
    ...vars,
    riskFilterConditions: useEntityWhereFilter<Risk_Bool_Exp>(
      Parent_Type_Enum.Risk,
      vars.riskFilterConditions
    ),
    actionFilterConditions: useEntityWhereFilter<Action_Bool_Exp>(
      Parent_Type_Enum.Action,
      vars.actionFilterConditions
    ),
    indicatorFilterConditions: useEntityWhereFilter<Indicator_Bool_Exp>(
      Parent_Type_Enum.Indicator,
      vars.indicatorFilterConditions
    ),
    documentFilterConditions: useEntityWhereFilter<Document_Bool_Exp>(
      Parent_Type_Enum.Document,
      vars.documentFilterConditions
    ),
    assessmentFilterConditions: useEntityWhereFilter<Assessment_Bool_Exp>(
      Parent_Type_Enum.Assessment,
      vars.assessmentFilterConditions
    ),
    issueFilterConditions: useEntityWhereFilter<Issue_Bool_Exp>(
      Parent_Type_Enum.Issue,
      vars.issueFilterConditions
    ),
    internalAuditFilterConditions:
      useEntityWhereFilter<Internal_Audit_Entity_Bool_Exp>(
        Parent_Type_Enum.InternalAuditEntity,
        vars.internalAuditFilterConditions
      ),
    obligationFilterConditions: useEntityWhereFilter<Obligation_Bool_Exp>(
      Parent_Type_Enum.Obligation,
      vars.obligationFilterConditions
    ),
    thirdPartyFilterConditions: useEntityWhereFilter<Third_Party_Bool_Exp>(
      Parent_Type_Enum.ThirdParty,
      vars.thirdPartyFilterConditions
    ),
    controlFilterConditions: useEntityWhereFilter<Control_Bool_Exp>(
      Parent_Type_Enum.Control,
      vars.controlFilterConditions
    ),
  };
};
